import ApiService from "@/services/api.service";
import { handleVuexError, handleVuexSuccess } from "@/js/errorHandler";

// Numero progressivo dell'ultima richiesta dell'elenco messaggi (vedi PAGINATED_MAIL_LIST)
let ultimaRichiestaElenco = 0;

const READ_ONLY_MAILBOX_MESSAGE =
    "La casella virtuale selezionata è in sola lettura";

const getMailboxById = (state, idMailVirtuale) => {
    if (!idMailVirtuale) return null;
    return (
        state.listMailAssociate?.find(
            ({ idMail }) => String(idMail) === String(idMailVirtuale)
        ) ||
        state.listMailAssegnabili?.find(
            ({ idMail }) => String(idMail) === String(idMailVirtuale)
        ) ||
        state.listMailAssociateDdl?.find(
            ({ idMail, value }) =>
                String(idMail ?? value) === String(idMailVirtuale)
        ) ||
        null
    );
};

const isReadOnlyMailbox = (state, getters, idMailVirtuale) => {
    if (idMailVirtuale) {
        const mailbox = getMailboxById(state, idMailVirtuale);
        return Boolean(mailbox?.solaLettura);
    }

    return Boolean(getters.getSelectedMail?.solaLettura);
};

/**
 * CP-381: il messaggio aperto e' una ricevuta? Nella vista a conversazioni conta il tipo del
 * messaggio (puo' essere un'inviata mostrata in una cartella di ricevute, o viceversa); altrimenti
 * resta la regola di sempre, basata sulla cartella selezionata.
 */
const isRicevutaAperta = (state, daCartella) =>
    state.tipoDettaglio ? state.tipoDettaglio === "ricevuta" : Boolean(daCartella);

const blockReadOnlyAction = (commit) => {
    commit("SET_ERROR", READ_ONLY_MAILBOX_MESSAGE);
    setTimeout(() => commit("SET_ERROR", ""), 5000);
    return Promise.reject(new Error(READ_ONLY_MAILBOX_MESSAGE));
};

export default {
    namespaced: true,
    state: {
        listMailAssociate: [],
        listMailAssegnabili: [],
        listMailAssociateDdl: [],
        listMailVirtuali: [],
        listCounterFolders: [],
        selectedMail: null,
        detail: {},
        /**
         * CP-381: tipo ("ricevuta" | "inviata") del messaggio aperto dalla vista a conversazioni,
         * dove puo' essere diverso dal tipo della cartella selezionata. null = decide la cartella.
         */
        tipoDettaglio: null,
        /** CP-381: cartella del messaggio aperto dalla vista a conversazioni (null = quella selezionata). */
        cartellaDettaglio: null,
        loading: false,
        success: {},
        error: {},
        inoltraRicevute: {},
        inoltraInviate: {},
        replyRicevuta: {},
        globalLoading: false,
    },
    getters: {
        getListMailAssociate: (state) => state.listMailAssociate,
        getListMailAssegnabili: (state) => state.listMailAssegnabili,
        getListCounterFolders: (state) => state.listCounterFolders,
        getListMailAssociateDdl: (state) => state.listMailAssociateDdl,
        getListMailVirtuali: (state) => state.listMailVirtuali,
        getSelectedMail: (state) => {
            if (state.selectedMail) return state.selectedMail;
            else if (state.listMailAssociate?.length)
                return {
                    idMailVirtuale: state.listMailAssociate[0].idMail,
                    solaLettura: state.listMailAssociate[0].solaLettura,
                    // GES-1: la casella selezionata di default può essere una aggregata (compare per prima)
                    aggregata: Boolean(state.listMailAssociate[0].aggregata),
                    origini: state.listMailAssociate[0].aggregata
                        ? state.listMailAssociate[0].origini || []
                        : [],
                    ...state.listMailAssociate[0]?.cartelle[0],
                };
        },
        getDetail: (state) => state.detail,
        getTipoDettaglio: (state) => state.tipoDettaglio,
        getNomeCartellaDettaglio: (state, getters) =>
            state.cartellaDettaglio || getters.getSelectedMail?.nomeCartella || null,
        getLoading: (state) => state.loading,
        success: (state) => state.success,
        error: (state) => state.error,
        getInoltraRicevute: (state) => state.inoltraRicevute,
        getInoltraInviate: (state) => state.inoltraInviate,
        getReplyRicevuta: (state) => state.replyRicevuta,
        globalLoading: (state) => state.globalLoading,
    },
    mutations: {
        SET_MAIL_ASSOCIATE(state, listMailAssociate) {
            state.listMailAssociate = [...listMailAssociate];
        },
        SET_MAIL_ASSEGNABILI(state, listMailAssegnabili) {
            state.listMailAssegnabili = [...listMailAssegnabili];
        },
        SET_MAIL_ASSOCIATE_DDL(state, listMailAssociateDdl) {
            state.listMailAssociateDdl = listMailAssociateDdl;
        },
        SET_MAIL_VIRTUALI_LIST(state, listMailVirtuali) {
            state.listMailVirtuali = listMailVirtuali;
        },
        SET_SELECTED_MAIL(state, item) {
            state.selectedMail = item;
            state.tipoDettaglio = null;
            state.cartellaDettaglio = null;
        },
        SET_TIPO_DETTAGLIO(state, payload) {
            const tipo = payload?.tipo;
            state.tipoDettaglio = tipo === "ricevuta" || tipo === "inviata" ? tipo : null;
            state.cartellaDettaglio = state.tipoDettaglio ? payload?.nomeCartella || null : null;
        },
        SET_LIST_COUNTER_FOLDER(state, listCounterFolders) {
            state.listCounterFolders = listCounterFolders;
        },
        SET_DETAIL_SELECTED_MAIL(state, item) {
            state.detail = item;
        },
        SET_LOADING(state, val) {
            state.loading = val;
        },
        SET_GLOBAL_LOADING(state, val) {
            state.globalLoading = val;
        },
        SET_ERROR(state, val) {
            state.error = val;
        },
        SET_SUCCESS(state, val) {
            state.success = val;
        },
    },
    actions: {
        GET_ASSOCIATE_UTENTE({ commit }) {
            commit("SET_MAIL_ASSOCIATE", []);
            commit("SET_MAIL_ASSEGNABILI", []);
            commit("SET_MAIL_ASSOCIATE", []);
            commit("SET_SELECTED_MAIL", null);
            commit("SET_MAIL_VIRTUALI_LIST", []);

            return new Promise((resolve) => {
                ApiService.get(`email/getAssociateUtente`)
                    .then(({ data }) => {
                        commit("SET_MAIL_ASSOCIATE", data.data);

                        resolve(data);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                    });
            });
        },
        GET_MAIL_ASSEGNABILI_PER_CASELLA({ commit }, idMailVirtualeSorgente) {
            if (!idMailVirtualeSorgente) {
                commit("SET_MAIL_ASSEGNABILI", []);
                return Promise.resolve({ data: [] });
            }

            return new Promise((resolve, reject) => {
                ApiService.get(
                    `MailVirtuali/listAssignableByIdMailVirtuale?idMailVirtuale=${idMailVirtualeSorgente}`
                )
                    .then(({ data }) => {
                        commit("SET_MAIL_ASSEGNABILI", data?.data || []);
                        resolve(data);
                    })
                    .catch(({ response }) => {
                        commit("SET_MAIL_ASSEGNABILI", []);
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },
        GET_LIST_CARTELLE({ commit }, idMailVirtuale) {
            let qs = idMailVirtuale
                ? `MailVirtuali/getFoldersList?idMailVirtuale=${idMailVirtuale}`
                : "MailVirtuali/getFoldersList";
            return new Promise((resolve) => {
                ApiService.get(`${qs}`)
                    .then(({ data }) => {
                        resolve(data);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                    });
            });
        },
        GET_LIST_COUNTER_FOLDERS({ commit }, idMailVirtuale) {
            let qs = `email/mailCounterByIdMailVirtuale/${idMailVirtuale}`;

            return new Promise((resolve) => {
                ApiService.get(`${qs}`)
                    .then(({ data }) => {
                        commit("SET_LIST_COUNTER_FOLDER", data.data);
                        resolve(data);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                    });
            });
        },
        GET_ASSOCIATE_DDL({ commit }) {
            return new Promise((resolve) => {
                ApiService.get(`email/getAssociateUtenteDdl`)
                    .then(({ data }) => {
                        commit("SET_MAIL_ASSOCIATE_DDL", data.data);
                        resolve(data);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                    });
            });
        },
        PAGINATED_MAIL_LIST({ commit, getters }, query) {
            if (getters.getSelectedMail) {
                commit("SET_LOADING", true);
                // Cambiando casella in fretta, la risposta di una richiesta precedente può arrivare dopo quella
                // attuale e riempire la lista con i messaggi della casella sbagliata (in una aggregata la colonna
                // "Casella" resta vuota): vale solo l'ultima richiesta fatta.
                const numeroRichiesta = ++ultimaRichiestaElenco;

                return new Promise((resolve) => {
                    ApiService.get(
                        `email/paginated/${getters.getSelectedMail.idCartella}?${query}`
                    )
                        .then(({ data }) => {
                            if (numeroRichiesta === ultimaRichiestaElenco) {
                                commit("SET_MAIL_VIRTUALI_LIST", data);
                                commit("SET_LOADING", false);
                            }

                            resolve(data);
                        })
                        .catch((res) => {
                            commit("SET_ERROR", res.data);
                            commit("SET_LOADING", false);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            }
        },
        FETCH_LIST_MAIL_VIRTUALI({ commit }) {
            commit("SET_LOADING", true);

            return new Promise((resolve) => {
                ApiService.get(`email/listDdl`)
                    .then((res) => {
                        commit("SET_MAIL_VIRTUALI_LIST", res.data);
                        commit("SET_LOADING", false);
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => {
                            commit("SET_ERROR", "");
                            commit("SET_LOADING", false);
                        }, 5000);
                    });
            });
        },
        SEND_EMAIL({ commit, dispatch, state, getters }, params) {
            if (isReadOnlyMailbox(state, getters, params?.IdMailVirtuale)) {
                return blockReadOnlyAction(commit);
            }

            commit("SET_GLOBAL_LOADING", true);

            let formData = new FormData();
            formData.append("Id", params.Id || "");
            formData.append("IdMailVirtuale", params.IdMailVirtuale);
            formData.append("IdMailRicevuta", params.IdMailRicevuta || "");
            formData.append("Destinatari", params.Destinatari);
            formData.append("DestinatariCc", params.DestinatariCc);
            formData.append("Oggetto", params.Oggetto);
            formData.append("CorpoDelMessaggio", params.CorpoDelMessaggio);
            formData.append(
                "CorpoDelMessaggioHtml",
                params.CorpoDelMessaggioHtml
            );
            if (params.Allegati?.length)
                params.Allegati.forEach((file) => {
                    formData.append("Allegati", file, file.name);
                });
            if (params.AllegatiOld?.length) {
                params.AllegatiOld.forEach((allegato, index) => {
                    formData.append(`allegatiOld[${index}].id`, allegato.id);
                    formData.append(
                        `allegatiOld[${index}].nomeAllegato`,
                        allegato.nomeAllegato
                    );
                    formData.append(
                        `allegatiOld[${index}].isRicevuta`,
                        allegato.isRicevuta
                    );
                    formData.append(
                        `allegatiOld[${index}].nomeAllegatoUnivoco`,
                        allegato.nomeAllegatoUnivoco
                    );
                    formData.append(
                        `allegatiOld[${index}].percorsoCartella`,
                        allegato.percorsoCartella
                    );
                    formData.append(
                        `allegatiOld[${index}].estensione`,
                        allegato.estensione
                    );
                    formData.append(
                        `allegatiOld[${index}].codifica`,
                        allegato.codifica
                    );
                    formData.append(
                        `allegatiOld[${index}].idEmailInviate`,
                        allegato.idEmailInviate
                    );
                    formData.append(
                        `allegatiOld[${index}].dataUltimaModifica`,
                        allegato.dataUltimaModifica
                    );
                    formData.append(
                        `allegatiOld[${index}].eliminato`,
                        allegato.eliminato
                    );
                });
            }

            formData.append("Stato", params.Stato);

            return new Promise((resolve, reject) => {
                ApiService.upload(`email/invia`, formData)
                    .then((res) => {
                        handleVuexSuccess({ commit, dispatch, state: this.state }, "Email inviata con successo", "SET_GLOBAL_LOADING");
                        resolve(res);
                    })
                    .catch((error) => {
                        handleVuexError({ commit, dispatch, state: this.state }, error, "Errore nell'invio dell'email", "SET_GLOBAL_LOADING");
                        reject(error);
                    });
            });
        },
        SEND_BOZZA({ commit, state, getters }, params) {
            if (isReadOnlyMailbox(state, getters, params?.IdMailVirtuale)) {
                return blockReadOnlyAction(commit);
            }

            commit("SET_GLOBAL_LOADING", true);

            let formData = new FormData();
            formData.append("Id", params.Id || "");
            formData.append("IdMailVirtuale", params.IdMailVirtuale);
            formData.append("Destinatari", params.Destinatari);
            formData.append("DestinatariCc", params.DestinatariCc);
            formData.append("Oggetto", params.Oggetto);
            formData.append("CorpoDelMessaggio", params.CorpoDelMessaggio);
            formData.append(
                "CorpoDelMessaggioHtml",
                params.CorpoDelMessaggioHtml
            );

            params.Allegati.forEach((file) => {
                formData.append("Allegati", file, file.name);
            });

            formData.append("Stato", params.Stato);

            return new Promise((resolve, reject) => {
                ApiService.upload(`email/salvaBozza`, formData)
                    .then((res) => {
                        commit("SET_GLOBAL_LOADING", false);

                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_GLOBAL_LOADING", false);
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        GET_MAIL({ commit, state, getters }, payload) {
            // CP-381: dalla vista a conversazioni arriva { id, tipo } e il tipo del messaggio
            // decide l'endpoint (e le azioni del dettaglio); con il solo id decide la cartella.
            const conTipo = payload !== null && typeof payload === "object";
            const id = conTipo ? payload.id : payload;
            commit("SET_TIPO_DETTAGLIO", conTipo ? payload : null);
            if (
                isRicevutaAperta(
                    state,
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "posta in arrivo" ||
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "cartella" ||
                    getters.getSelectedMail?.cartellaPostaInArrivo
                )
            ) {
                return new Promise((resolve) => {
                    console.log('Fetching received mail with ID:', id);
                    ApiService.get(`email/ricevuta/${id}`)
                        .then(({ data }) => {
                            console.log('Received mail response:', data);
                            commit("SET_DETAIL_SELECTED_MAIL", data.data || data);
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data || data);
                        })
                        .catch(({ response }) => {
                            console.error('Error fetching received mail:', response);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                            commit("SET_GLOBAL_LOADING", false);
                        });
                });
            } else {
                return new Promise((resolve) => {
                    console.log('Fetching sent mail with ID:', id);
                    ApiService.get(`email/inviata/${id}`)
                        .then(({ data }) => {
                            console.log('Sent mail response:', data);
                            commit("SET_DETAIL_SELECTED_MAIL", data.data || data);
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data || data);
                        })
                        .catch(({ response }) => {
                            console.error('Error fetching sent mail:', response);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                            commit("SET_GLOBAL_LOADING", false);
                        });
                });
            }
        },
        UPDATE_MAIL({ commit }, params) {
            commit("SET_GLOBAL_LOADING", true);
            return new Promise((resolve, reject) => {
                ApiService.update(`email`, params.id, params)
                    .then((res) => {
                        commit("SET_GLOBAL_LOADING", false);
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_GLOBAL_LOADING", false);
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        CREATE_FOLDER({ commit, getters }, folderName) {
            // SE è ROOT FOLDER ALLORA ID PADRE = 0
            //ALTRIMENTI ID PADRE = ID DELLA CARTELLA SELEZIONATA
            const obj = {
                idMailVirtuale: getters.getSelectedMail.idMailVirtuale,
                idCartellaPadre: getters.getSelectedMail.idCartella,
                nomeCartella: folderName,
            };

            return new Promise((resolve, reject) => {
                ApiService.post(`email/createFolder`, obj)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        UPDATE_FOLDER({ commit, getters }, folderName) {
            const obj = {
                idFolder: getters.getSelectedMail.idCartella,
                newName: folderName,
            };

            return new Promise((resolve, reject) => {
                ApiService.update(`email/updateFolderName`, obj.idFolder, obj)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        READ_MAIL({ commit, state, getters }, params) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            return new Promise((resolve, reject) => {
                ApiService.update(`email/daLeggere`, params.id)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        LETTA_BY_FOLDER({ commit, state, getters }, idFolder) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            return new Promise((resolve, reject) => {
                ApiService.put(`email/lettaByFolder/${idFolder}`, {})
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        MOVE_MAIL({ commit, state, getters }, payload) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            return new Promise((resolve, reject) => {
                ApiService.post(`email/sposta`, payload)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },
        MOVE_MAIL_BULK({ commit, state, getters }, payload) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            return new Promise((resolve, reject) => {
                ApiService.post(`email/spostaBulk`, payload)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },
        REINVIA_MAIL({ commit, state, getters }, id) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            return new Promise((resolve, reject) => {
                ApiService.post(`email/reinvia?id=${encodeURIComponent(id)}`)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },
        ASSEGNA_MAIL({ commit, state, getters }, payload) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            return new Promise((resolve, reject) => {
                ApiService.post(`email/assegna`, payload)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },
        ASSEGNA_ALL({ commit, state, getters }, payload) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            return new Promise((resolve, reject) => {
                ApiService.post(`email/assegnaTutte`, payload)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },
        DELETE_ALLEGATO({ commit }, id) {
            return new Promise((resolve, reject) => {
                ApiService.delete(`email/allegato`, id)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        DELETE_BOZZA({ commit }, id) {
            return new Promise((resolve, reject) => {
                ApiService.delete(`email/bozza`, id)
                    .then((res) => {
                        resolve(res);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                        reject(response);
                    });
            });
        },

        DOWNLOAD_EML_MAIL({ commit, state, getters }, id) {
            commit("SET_GLOBAL_LOADING", true);
            if (
                isRicevutaAperta(
                    state,
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "posta in arrivo" ||
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "cartella" ||
                    getters.getSelectedMail?.cartellaPostaInArrivo
                )
            ) {
                return new Promise((resolve) => {
                    ApiService.generate(`email/downloadEmlRicevuta/${id}`)
                        .then(({ data }) => {
                            const fileName = `pec-${id}.eml`;
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data);

                            link.target = "_blank";
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data);
                        })
                        .catch(({ response }) => {
                            commit("SET_GLOBAL_LOADING", false);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            } else {
                return new Promise((resolve) => {
                    ApiService.generate(`email/downloadEmlInviata/${id}`)
                        .then(({ data }) => {
                            const fileName = `pec-${id}.eml`;
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data);

                            link.target = "_blank";
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data);
                        })
                        .catch(({ response }) => {
                            commit("SET_GLOBAL_LOADING", false);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            }
        },

        DOWNLOAD_BY_PATH_EML({ commit, getters }, payload) {
            commit("SET_GLOBAL_LOADING", true);
            const destinatario = getters.getDetail?.destinatari?.find(
                ({ id }) => id === payload?.idDestinatario
            );
            let path = "";
            if (payload.type === "consegna")
                path = destinatario?.esitoConsegna?.pathEml;
            else path = destinatario?.accettazione?.pathEml;

            return new Promise((resolve) => {
                ApiService.generate(`email/downloadEmlByPath?pathEml=${path}`)
                    .then(({ data }) => {
                        const fileName =
                            payload.type === "consegna"
                                ? destinatario?.esitoConsegna?.nomeFileEml
                                : destinatario?.accettazione?.nomeFileEml;
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(data);

                        link.target = "_blank";
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        commit("SET_GLOBAL_LOADING", false);
                        resolve(data);
                    })
                    .catch(({ response }) => {
                        commit("SET_GLOBAL_LOADING", false);
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                    });
            });
        },

        DOWNLOAD_ALLEGATI_MAIL({ commit, state, getters }, payload) {
            commit("SET_GLOBAL_LOADING", true);
            if (
                isRicevutaAperta(
                    state,
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "posta in arrivo" ||
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "cartella" ||
                    getters.getSelectedMail?.cartellaPostaInArrivo
                )
            ) {
                return new Promise((resolve) => {
                    ApiService.downloadWithParam(
                        `email/allegato/ricevuta`,
                        payload.fileId,
                        payload.name
                    )
                        .then(({ data }) => {
                            const fileName = `${payload.name}`;
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data);
                            link.target = "_blank";
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_GLOBAL_LOADING", false);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            } else {
                return new Promise((resolve) => {
                    ApiService.downloadWithParam(
                        `email/allegato/inviata`,
                        payload.fileId,
                        payload.name
                    )
                        .then(({ data }) => {
                            const fileName = `${payload.name}`;
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data);
                            link.target = "_blank";
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_GLOBAL_LOADING", false);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            }
        },

        DOWNLOAD_ALL_ALLEGATI_MAIL({ commit, state, getters }, idMail) {
            commit("SET_GLOBAL_LOADING", true);
            if (
                isRicevutaAperta(
                    state,
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "posta in arrivo" ||
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "cartella" ||
                    getters.getSelectedMail?.cartellaPostaInArrivo
                )
            ) {
                return new Promise((resolve) => {
                    ApiService.customGet(
                        `email/downloadAllegatiRicevuta/`,
                        idMail
                    )
                        .then((data) => {
                            const fileName = `allegati_${idMail}`;
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data.data);
                            link.target = "_blank";
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_GLOBAL_LOADING", false);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            } else {
                return new Promise((resolve) => {
                    ApiService.customGet(
                        `email/downloadAllegatiInviata/`,
                        idMail
                    )
                        .then((data) => {
                            const fileName = `allegati_${idMail}`;
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data.data);
                            link.target = "_blank";
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_GLOBAL_LOADING", false);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            }
        },

        INOLTRA_MAIL({ commit, state, getters }, id) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            commit("SET_GLOBAL_LOADING", true);

            if (
                isRicevutaAperta(
                    state,
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "posta in arrivo" ||
                    getters.getSelectedMail?.cartellaPostaInArrivo
                )
            ) {
                state.inoltraInviate = {};

                return new Promise((resolve) => {
                    ApiService.download(`email/ricevute/inoltra`, id)
                        .then(({ data }) => {
                            state.inoltraInviate = data.data;
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_GLOBAL_LOADING", false);
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            } else {
                state.inoltraInviate = {};

                return new Promise((resolve) => {
                    ApiService.download(`email/inviate/inoltra`, id)
                        .then(({ data }) => {
                            state.inoltraInviate = data.data;
                            commit("SET_GLOBAL_LOADING", false);
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                            commit("SET_GLOBAL_LOADING", false);
                        });
                });
            }
        },
        REPLY_MAIL({ commit, state, getters }, id) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            if (
                isRicevutaAperta(
                    state,
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "posta in arrivo" ||
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "cartella" ||
                    getters.getSelectedMail?.cartellaPostaInArrivo
                )
            ) {
                state.replyRicevuta = {};

                return new Promise((resolve) => {
                    ApiService.download(`email/rispondi`, id)
                        .then(({ data }) => {
                            state.replyRicevuta = data.data;
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            }
        },

        REPLY_ALL_MAIL({ commit, state, getters }, id) {
            if (isReadOnlyMailbox(state, getters)) {
                return blockReadOnlyAction(commit);
            }

            if (
                isRicevutaAperta(
                    state,
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "posta in arrivo" ||
                    getters.getSelectedMail?.tipoCartella.toLowerCase() ===
                        "cartella" ||
                    getters.getSelectedMail?.cartellaPostaInArrivo
                )
            ) {
                state.replyRicevuta = {};

                return new Promise((resolve) => {
                    ApiService.download(`email/rispondiTutti`, id)
                        .then(({ data }) => {
                            state.replyRicevuta = data.data;
                            resolve(data.data);
                        })
                        .catch(({ response }) => {
                            commit("SET_ERROR", response?.data?.message);
                            setTimeout(() => commit("SET_ERROR", ""), 5000);
                        });
                });
            }
        },

        DELETE_FOLDER({ commit, getters }) {
            return new Promise((resolve) => {
                ApiService.delete(
                    `email/deleteFolder`,
                    getters.getSelectedMail.idCartella
                )
                    .then(({ data }) => {
                        commit("SET_SELECTED_MAIL", null);
                        resolve(data.data);
                    })
                    .catch(({ response }) => {
                        commit("SET_ERROR", response?.data?.message);
                        setTimeout(() => commit("SET_ERROR", ""), 5000);
                    });
            });
        },

        GET_MAIL_ATTIVITA(_, idMailRicevuta) {
            return new Promise((resolve, reject) => {
                ApiService.get(`email/attivita/${idMailRicevuta}`)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(({ response }) => {
                        reject(response);
                    });
            });
        },
    },
};
